"use client";

import { Droplet, Flame, Flashlight, Search, ShowerHead, Wrench } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function OurResidentialTreeServices() {
  const header = useRevealOnScroll<HTMLDivElement>({
    once: true,
    threshold: 0.2
  });
  const services = [{
    code: "[ T-01 ]",
    title: "Professional Tree Trimming",
    description: "Expert canopy thinning and crown reduction to promote healthy growth and enhance the natural beauty of your landscape.",
    Icon: Wrench
  }, {
    code: "[ T-02 ]",
    title: "Safe Tree Removal",
    description: "Precision removal of hazardous or unwanted trees using advanced rigging techniques to ensure property safety.",
    Icon: ShowerHead
  }, {
    code: "[ T-03 ]",
    title: "Stump Grinding",
    description: "Complete removal of unsightly stumps, allowing you to reclaim your yard space and prevent pest infestations.",
    Icon: Droplet
  }, {
    code: "[ T-04 ]",
    title: "Health Assessments",
    description: "Comprehensive diagnostic evaluations to identify diseases, pests, or structural weaknesses in your mature trees.",
    Icon: Search
  }, {
    code: "[ T-05 ]",
    title: "Emergency Storm Care",
    description: "24/7 rapid response for storm-damaged trees, clearing debris and securing your property after severe weather events.",
    Icon: Flashlight
  }, {
    code: "[ T-06 ]",
    title: "Deep Root Fertilization",
    description: "Custom nutrient injections delivered directly to the root zone to boost vitality and resilience against environmental stress.",
    Icon: Flame
  }];
  return <section data-section-id="994" id="our-residential-tree-services" className="bg-background text-foreground py-32">
      <div className="mx-auto max-w-7xl px-6 lg:px-8">
        {}
        <div ref={header.ref} className={`grid grid-cols-1 gap-10 lg:grid-cols-2 lg:gap-16 transition-all duration-700 ease-out ${header.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          <div>
            <p className="font-mono text-xs tracking-widest text-muted-foreground uppercase mb-6">
              [ 01 &mdash; SERVICES ]
            </p>
            <h2 className="font-serif font-default text-foreground leading-[1.05] tracking-tight" style={{
            fontSize: "clamp(2.5rem, 5vw, 4rem)"
          }}>
              Every branch, every root,{" "}
              <em className="italic font-serif text-primary">we care for.</em>
            </h2>
          </div>
          <div className="flex flex-col justify-end gap-6">
            <p className="text-base lg:text-lg text-muted-foreground leading-relaxed max-w-md">
              From routine maintenance to complex removals, our certified arborists deliver professional care backed by safety standards and a commitment to your landscape's longevity.
            </p>
            <div className="flex flex-wrap items-center gap-6">
              <Link to="/OurExpertisePage" className="group inline-flex items-center gap-2 font-mono text-sm text-foreground hover:text-primary transition-colors w-fit">
                <span className="border-b border-foreground/30 group-hover:border-primary pb-0.5">
                  View all services
                </span>
                <span className="transition-transform group-hover:translate-x-1">
                  &rarr;
                </span>
              </Link>
              <Link to="/GetQuotePage" className="group inline-flex items-center gap-2 font-mono text-sm text-primary hover:text-foreground transition-colors w-fit">
                <span className="border-b border-primary group-hover:border-foreground pb-0.5">
                  Get a Quote
                </span>
                <span className="transition-transform group-hover:translate-x-1">
                  &rarr;
                </span>
              </Link>
            </div>
          </div>
        </div>

        {}
        <div className="mt-20 grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
          {services.map((service, i) => {
          const Icon = service.Icon;
          return <div key={i} data-index={i} className="group relative flex flex-col bg-card border border-border rounded-lg p-10 transition-all duration-300 hover:border-primary overflow-hidden shadow-md">
                {}
                <div className="mb-10">
                  <Icon className="text-primary transition-transform duration-300 group-hover:scale-105" strokeWidth={1} style={{
                width: "64px",
                height: "64px"
              }} />
                </div>

                {}
                <p className="font-mono text-xs tracking-widest text-muted-foreground uppercase mb-4">
                  {service.code}
                </p>

                {}
                <h3 className="font-serif font-default text-2xl text-card-foreground leading-tight mb-4">
                  {service.title}
                </h3>

                {}
                <p className="font-default text-[15px] leading-relaxed text-muted-foreground mb-8">
                  {service.description}
                </p>

                {}
                <div className="mt-auto pt-6 border-t border-border">
                  {service.code === "[ T-01 ]" ? <Link to="/GetQuotePage" className="inline-flex items-center gap-1.5 font-mono text-xs text-primary hover:gap-2.5 transition-all">
                      <span>Request a Quote</span>
                      <span>&rarr;</span>
                    </Link> : service.code === "[ T-04 ]" ? <Link to="/HoaTreeCarePage" className="inline-flex items-center gap-1.5 font-mono text-xs text-primary hover:gap-2.5 transition-all">
                      <span>Learn more about HOA Tree Care</span>
                      <span>&rarr;</span>
                    </Link> : <Link to="/OurExpertisePage" className="inline-flex items-center gap-1.5 font-mono text-xs text-primary hover:gap-2.5 transition-all">
                      <span>Learn more</span>
                      <span>&rarr;</span>
                    </Link>}
                </div>

                {}
                <span className="absolute bottom-0 left-0 h-px w-0 group-hover:w-full transition-all duration-500 ease-out" style={{
              backgroundColor: "var(--accent)"
            }} aria-hidden="true" />
              </div>;
        })}
        </div>

        {}
        <div className="mt-12 flex justify-center lg:hidden">
          <Button asChild variant="outline" size="lg" className="font-mono text-sm">
            <Link to="/OurExpertisePage">View all services &rarr;</Link>
          </Button>
        </div>
      </div>
    </section>;
}